/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState, useCallback, useContext } from "react";
import { Form, Formik, FormikConfig, FormikHelpers } from "formik";
import classNames from "classnames";
import { Grid, Typography } from "@material-ui/core";
import { useMutation } from "@apollo/client";
import * as yup from "yup";
import { navigate, useLocation } from "@reach/router";
import { Card } from "components/haven-design";
import { Button, LinkButton } from "components/haven-design/buttons";
import { Login as LoginContext } from "components/viewer/context";
import { getVisitorId, initializeUserLogin } from "services/analytics";
import { UserLoginPreflight, UserLoginPreflightVariables, LoginMethod, UserLogin, UserLoginVariables } from "types/gql-types";
import { getReturnToUrl } from "utils/login";
import { formatPhone } from "utils/phone";
import { loginWithHavenToken } from "utils/general-utils";
import { useElixirLoginFeature } from "components/feature/use-elixir-login-feature";
import { LoginFormValues, Section, Handle, Page, PageType } from "../type";
import { isEmailIdentifier } from "../utils";
import { SIGN_IN_ATTEMPT, USER_LOGIN } from "../query";
import { onSendSMSVerification, onLoginSMS, onLoginEmail } from "../meteor";
import LoginModalContext from "../context/modal";
import styles from "./Login.module.css";
import { loginStyles } from "./styles";
import { LoginCardTitle, LoginCardSubtitle, LoneGrid } from "./shared-components";
import SignUpCard from "./sign-up-card";
import WaitToVerify from "./wait-to-verify";
import LoginFormMobile from "./login-form-mobile";
import LoginForm from "./login-form";
import { useRememberedEmail } from "./hooks";

const components: { [K in Handle<PageType>["page"]]: Page<K> } = {
  create: SignUpCard,
  "wait-to-verify": WaitToVerify,
  login: () => null,
};

interface LoginContainerProps {
  phoneNumberAwaitingCode: string | false;
  headerText?: string | React.ReactNode;
  subheaderText?: string;
}

const LoginFormContainer: React.FC<LoginContainerProps> = (props) => {
  const { inModal } = useContext(LoginModalContext);

  return (
    <Form className={styles.form}>
      <Card className={classNames(styles.loginCard, { [styles.borderlessCard]: inModal })}>
        <div style={{ margin: 16 }}>
          <Grid container spacing={2} direction="column" alignItems="center" justifyContent="center">
            <Grid item xs={12}>
              <LoginCardTitle title={props.headerText || "Welcome Back!" } />
              {(!props.headerText && !props.phoneNumberAwaitingCode) && <LoginCardSubtitle>
                Property managers and applicants can login here.
              </LoginCardSubtitle>}
              {props.phoneNumberAwaitingCode && (
                <LoginCardSubtitle>
                  Enter the 4-digit code sent to you at {formatPhone(props.phoneNumberAwaitingCode)}
                </LoginCardSubtitle>
              )}
              {props.subheaderText && (
                <Typography variant="body1" style={{
                  textAlign: "center",
                  fontSize: ".9rem",
                  paddingBottom: "0.8em",
                  paddingTop: 0,
                  fontWeight: 300,
                }}>{props.subheaderText}</Typography>)}
            </Grid>
            {props.children}
          </Grid>
        </div>
      </Card>
    </Form>
  );
};

const SignInLink: React.FC<{ onClick?: () => void; link?: boolean }> =
({ onClick, link = false }) => {
  const message = "Already have an account?";
  const buttonText = "Login to your Account";
  const location = useLocation();
  const returnToUrl = getReturnToUrl();
  const retunToCurrent = returnToUrl ? `?return-to=${returnToUrl}` : "";
  const loginLink = `/login${location.search || retunToCurrent}`;
  return (
    <LoneGrid>
      <Typography variant="body2" style={loginStyles.signUpLabel}>
        {message}
      </Typography>

      {link ? (
        <LinkButton color="secondary" size="L" fullWidth to={loginLink}>
          {buttonText}
        </LinkButton>
      ) : (
        <Button
          id="sign-up"
          type="button"
          color="secondary"
          size="L"
          className={styles.loginButton}
          onClick={onClick}
        >
          {buttonText}
        </Button>)}
    </LoneGrid>
  );
};

const processValues = (values: LoginFormValues) => ({
  ...values,
  identifier: values.identifier.trim(),
});

const onDisplayError = (handlers: FormikHelpers<LoginFormValues>, error: unknown) => {
  let errorMessage = "";

  if (error instanceof Error) errorMessage = (error as Error).message;
  else errorMessage = error as string;

  const errorElementIds = errorMessage.toLowerCase().includes("password") ? ["password"] : ["identifier", "code"];
  errorElementIds.forEach((element) => handlers.setFieldError(element, errorMessage));
};

const schema = yup.object({
  identifier: yup.string().required("A valid email or US mobile phone number is required."),

  password: yup
    .mixed()
    .when("identifier", { is: isEmailIdentifier, then: yup.string().required() }),

  remember: yup
    .mixed()
    .when("identifier", { is: isEmailIdentifier, then: yup.bool() }),

  code: yup
    .mixed()
    .when("identifier", { is: isEmailIdentifier, otherwise: yup.string() }),
});

type LoginCardProps = {
  afterLogin: () => void,
  headerText?: string | React.ReactNode,
  subheaderText?: string;
  isSignup?: boolean;
  formId?: string;
}

const LoginCard: React.FC<LoginCardProps> = (coreProps) => {
  const useElixirLogin = useElixirLoginFeature();
  const { onLogin } = useContext(LoginContext);
  const [preflight] = useMutation<UserLoginPreflight, UserLoginPreflightVariables>(SIGN_IN_ATTEMPT);
  const [userLogin] = useMutation<UserLogin, UserLoginVariables>(USER_LOGIN);

  const { inModal } = useContext(LoginModalContext);

  const page = coreProps.isSignup ? "create" : "login";
  const [section, setSection] = useState<Section>({ page, props: {} });

  const [isAwaitingCode, setIsAwaitingCode] = useState<false | string>(false);
  const [rememberedEmail, wasEmailRemembered, rememberedEmailOps] = useRememberedEmail();
  const resetAwaitingCode = () => setIsAwaitingCode(false);

  const sendCode = async (phoneNumber: string, handlers: FormikHelpers<LoginFormValues>) => {
    try {
      await onSendSMSVerification(phoneNumber);
    } catch (err) {
      onDisplayError(handlers, err);
    }
  };

  const sharedProps = {
    onNavigate: (data: Section) => {
      setSection(data);
      if (!inModal && ["login", "create"].includes(data.page)) {
        const toPage = data.page === "login" ? "login" : "signup";
        navigate(`/${toPage}`);
      }
    },
    children: null as React.ReactNode,
  };

  const toSignUp = () =>
    sharedProps.onNavigate({
      page: "create",
      props: { },
    });

  const login = (userId: string) => {
    initializeUserLogin({
      userId,
      roles: [""],
      anonymousUserId: getVisitorId(),
    });

    onLogin();
    coreProps.afterLogin();
  };

  const loginWithElixir = async (processedValues:LoginFormValues) => {
    const {
      data: loginData,
    } = await userLogin({ variables: {
      username: processedValues.identifier,
      password: processedValues.password,
    } });
    const token = loginData?.userLogin.token ?? null;
    if (token) {
      loginWithHavenToken(token);
      login(token);
    }
  };

  const onSubmitWithPreflight: FormikConfig<LoginFormValues>["onSubmit"] = useCallback(async (values, handlers) => {
    const processedValues = processValues(values);

    if (isAwaitingCode) {
      try {
        const userId = await onLoginSMS(processedValues.identifier, processedValues.code);
        login(userId);
      } catch (err) {
        onDisplayError(handlers, err);
      }

      return;
    }

    const {
      data: result,
      errors,
    } = await preflight({ variables: { username: processedValues.identifier } });

    if (!result?.userLoginPreflight || errors) {
      onDisplayError(handlers, "Result is unexpectedly empty.");
      return;
    }

    const { userLoginPreflight } = result;

    if (userLoginPreflight.login === null) {
      onDisplayError(handlers, "No user account available");
      return;
    }

    switch (userLoginPreflight.login.method) {
      case LoginMethod.PHONE: {
        try {
          await onSendSMSVerification(processedValues.identifier);
          setIsAwaitingCode(processedValues.identifier);
        } catch (err) {
          onDisplayError(handlers, err);
        }

        break;
      }

      case LoginMethod.EMAIL: {
        rememberedEmailOps.update(processedValues.remember, processedValues.identifier);

        if (userLoginPreflight.login.verified) {
          try {
            if (useElixirLogin) {
              loginWithElixir(processedValues);
            } else {
              const userId =
                await onLoginEmail(processedValues.identifier, processedValues.password);
              login(userId);
            }
          } catch (err) {
            onDisplayError(handlers, err);
          }
          return;
        }

        sharedProps.onNavigate({
          page: "wait-to-verify",
          props: { username: processedValues.identifier, formId: coreProps.formId },
        });

        break;
      }

      default:
        break;
    }
  }, [useElixirLogin, isAwaitingCode]);

  if (section.page === "login") {
    return (
      <Formik
        enableReinitialize
        initialValues={{
          identifier: rememberedEmail,
          password: "",
          remember: wasEmailRemembered,
          code: "",
          showPassword: false,
        }}
        validationSchema={schema}
        onSubmit={onSubmitWithPreflight}>
        {(handlers) => (
          <LoginFormContainer
            phoneNumberAwaitingCode={isAwaitingCode}
            headerText={coreProps.headerText} subheaderText={coreProps.subheaderText} >
            {isAwaitingCode ? (
              <LoginFormMobile
                sendCode={() => sendCode(isAwaitingCode, handlers)}
                resetAwaitingCode={resetAwaitingCode}
              />
            ) : <LoginForm toSignUp={toSignUp} />}
          </LoginFormContainer>)

        }
      </Formik>
    );
  }

  const Component = components[section.page] as Page<PageType>;
  const childProps = { ...sharedProps, ...section.props };
  if (section.page === "create") {
    childProps.children = <SignInLink onClick={() => sharedProps.onNavigate({ page: "login", props: {} })} />;
  }

  return <Component {...childProps} />;
};

export default LoginCard;
